(ns practitest.frontend.components2.grid.fields
  (:require
   [clojure.set :as set]
   [clojure.string :as str]
   [practitest.frontend.common.dom :as dom]
   [practitest.frontend.common.format-utils :as fu]
   [practitest.frontend.common.i18n :refer [get-translation]]
   [practitest.frontend.components.grid :as oldgrid]
   [practitest.frontend.components.grid.filters :as filters]
   [practitest.frontend.components.grid.subs :refer [field->column]]
   [practitest.frontend.components.icons :as icons]
   [practitest.frontend.components2.spinners :as spinners]
   [practitest.frontend.components.markdown :refer [markdown-preview]]
   [practitest.frontend.components.overflow :as overflow]
   [practitest.frontend.components.tooltip :as tooltip]
   [practitest.frontend.components.users :as users]
   [practitest.frontend.components2.grid.common :as gc]
   [practitest.frontend.components2.pagination :as pagination]
   [practitest.frontend.components2.requirement-testing-status :as requirement-testing-status]
   [practitest.frontend.components2.run-status :as run-status]
   [practitest.frontend.components2.statuses-bar :refer [statuses-bar]]
   [practitest.frontend.components2.test-type :as test-type]
   [practitest.frontend.components2.value-score :as value-score]
   [practitest.frontend.global-fields-settings.common :refer [get-field-format-label]]
   [practitest.frontend.requirements.testing-coverage :as testing-coverage]
   [practitest.frontend.routing.backend-routes :refer [get-url-view]]
   [practitest.frontend.subs :as subs]
   [re-frame.core :as re-frame]))

(defn transform-fields
  ([visible-fields entity-fields fields users groups folders]
   (transform-fields visible-fields entity-fields fields users groups folders false))
  ([visible-fields entity-fields fields users groups folders show-rest?]
   (let [rest-fields (set/difference (set (keys entity-fields))
                                     (set visible-fields))
         conf-fn (fn [c show?]
                   (->> c
                        (mapv (fn [field]
                                (when-let [field-data (get fields field)]
                                  (assoc
                                    (field->column nil (assoc field-data
                                                         :users users
                                                         :groups groups
                                                         :folders folders))
                                    :default-show? show?))))
                        (remove nil?)))]
     (concat
       (conf-fn visible-fields true)
       (conf-fn rest-fields show-rest?)))))

(def simple-header gc/header-title)

(defn- safe-url? [url]
  (and url
       (not (str/blank? url))
       (not (str/starts-with? (str/lower-case url) "javascript:"))
       (not (str/starts-with? (str/lower-case url) "data:"))
       (not (str/starts-with? (str/lower-case url) "vbscript:"))))

(defn- parse-html-link [text]
  (let [container (.createElement js/document "div")]
    (set! (.-innerHTML container) text)
    (when-let [a (.-firstChild container)]
      (when (= "A" (.-tagName a))
        {:href (.-href a)
         :target (.-target a)
         :title (.-textContent a)}))))

(defn parsed-cell
  [{:keys [field]} row]
  (let [text (str (get row field))]
    (if-let [{:keys [href target title]} (parse-html-link text)]
      (if (safe-url? href)
        [:a (cond-> {:href href}
                    target (assoc :target target)
                    (= target "_blank") (assoc :rel "noopener noreferrer"))
         title]
        title) ; Show as plain text if unsafe URL
      text)))

(defn simple-text
  [{:keys [field] :as column-info} row]
  (str (get row field)))


(defn vector-of-maps-text
  "Where the structure is vector of maps and we need the :text of each map"
  [{:keys [field] :as _column-info} row]
  (let [value (if (= field :linked_milestones)
                (map (fn [item]
                       (if (map? item)
                         {:id (or (:id item) (first (vals item)))
                          :text (or (:text item) (:name item) (str item))}
                         (let [[id name] item]
                           {:id id
                            :text name})))
                     (get row field))
                (get row field))
        val (->> value
                  (map :text)
                  (interpose ", "))]
    (if (empty? val)
      (str val)
      (str/join val))))

(defn tags-text
  [{:keys [field] :as _} row]
  (let [val (str/join ", " (get row field))]
  (if (empty? val)
    (str val)
    (str/join val))))

(defn labels-text
  [{:keys [field]} row]
  (let [labels (get row field)
        names (map :name labels)]
    (if (empty? labels)
      (get-translation "instance.version-selector.latest" "Latest")
      (str/join ", " names))))

(defn simple-cell
  [{:keys [field] :as column-info} row]
  (str (get row field)))

;; Simple fields
(doseq [type [:date :list :linkedlist]]
  (gc/register-field-type!
    type
    simple-header
    simple-text
    simple-cell))

(doseq [type [:field :text]]
  (gc/register-field-type!
    type
    simple-header
    simple-text
    parsed-cell))

;; Supports both number and number:number formats
(defn- try-parse-display-id
  [value]
  (if (string? value)
    ;; Return number itself for correct sorting behaviour
    (if-let [re-pair (re-find #"^(\d+):(\d+)$" value)]
      (fu/try-parse-number2 (nth re-pair 2) value)
      ;; Try to parse as number, fallback to value
      (fu/try-parse-number2 value value))
    ;; Most likely it's number already here (happens as well)
    value))

(defn- compare-display-id
  [{:keys [field type] :as column-info} direction row-a row-b]
  (let [a (try-parse-display-id (get row-a field))
        b (try-parse-display-id (get row-b field))
        [a b] (if (= (number? a) (number? b)) [a b] [(str a) (str b)])]
    (oldgrid/nulls-last-sort direction a b)))

;; Display ID - correct sorting
(gc/register-field-type!
  :display_id
  simple-header
  simple-text
  simple-cell
  gc/default-search-fn
  compare-display-id)

; display id can be number or string (Jira or Clickup)
; Panding 0 to the display-id will sort string currectly
(defn pad-display-id
  [s]
  (when s
    (let [s (str s)]
      (if (str/includes? s "-")
        (let [[pref num] (str/split s #"-")]
          (str pref (fu/format-integer num 30)))
        (fu/format-integer s 30)))))

(defn compare-external-or-display-id
  [{:keys [field] :as column-info} direction row-a row-b]
  (oldgrid/nulls-last-sort direction
                           (pad-display-id (get row-a field))
                           (pad-display-id (get row-b field))))

(gc/register-field-type!
 :external_id_or_display_id
 simple-header
 simple-text
 simple-cell
 gc/default-search-fn
 compare-external-or-display-id)

(defn- compare-integers
  [{:keys [field] :as column-info} direction row-a row-b]
  (let [value-a (get row-a field)
        value-b (get row-b field)]
    (oldgrid/nulls-last-sort direction
                             (fu/try-parse-number2 value-a value-a)
                             (fu/try-parse-number2 value-b value-b))))

(gc/register-field-type!
  :number
  simple-header
  (fn [{:keys [field] :as column-info} row]
    (get row field))
  simple-cell
  gc/default-search-fn
  compare-integers)

(gc/register-field-type!
  :linked_milestones
  simple-header
  (fn [{:keys [field] :as column-info} row]
      (str/join ", " (map last (get row field))))
  (fn [{:keys [field] :as column-info} row]
    (str/join ", " (map last (get row field)))))

(gc/register-field-type!
  :milestone
  simple-header
  (fn [{:keys [field] :as column-info} row]
    (let [value (or (:name (get row field))
                    (:name (get row :milestone_id)))]
      value))
  (fn [{:keys [field] :as column-info} row]
    (let [value (or (:name (get row field))
                    (:name (get row :milestone_id)))]
      value)))

;; column-info structure here:
;
;{
; ;; Mix of predefined and server-sent props:
;
; :default-width 200
; :width         200
; :field         :___f_6
; :type          :list
; :header        "Operating System"
; :show?         true
; :sortable?     true
; :status-colors {"CANCELED" "#ffd900",
;                 "N/A" "#3a87ad",
;                 "BLOCKED" "#c09853"}
; :multiple?     true
; :autofilter?   true
;
; :users         list of users (where applicable)
; :groups        list of groups (where applicable)
;
; ;; Populated by body-cell component
; :container-ref (atom ref-node)
; :editable?     false
; :editing?      false
; :saving?       false
; :editing-value "value"
; :on-edit-change (fn [newvalue])
; :on-edit-save   (fn [newvalue])
; :on-edit-stop   (fn [])
; }

;; Test Type
(gc/register-field-type!
  :test_type
  simple-header
  (fn [{:keys [field] :as column-info} row]
    (let [type (get row field)]
      (test-type/get-type-label type)))
  (fn [{:keys [field] :as column-info} row]
    (let [type (get row field)]
      [test-type/type-with-label type])))

(gc/register-field-type!
  :url
  simple-header
  (fn [field-data row]
    (let [{:keys [title]} row]
      (cond
        (not (str/blank? title))
        title

        :else
        (let [field-key (:field field-data)]
          (get row field-key)))))
  (fn [_ row]
    (let [{:keys [url title]} row]
      [:a (dom/add-test-id
            {:href url
             :draggable false}
            "grid-row-name-link")
       title])))

;; TODO: cleanup - no need to have separate url field type
(gc/register-field-type!
  :custom-field-url
  simple-header
  (fn
    [{:keys [field]} row]
    (let [value (get row field)]
      (cond 
        (map? value)
        (:title value) 
        
        :else
        (str value))))
  (fn [field-data row]
    (let [custom-field-key (:field field-data)
          value (get row custom-field-key)]
      (cond
        (string? value)
        [:a {:href value
             :target "_blank"} value]

        (map? value)
        (let [{:keys [url title]} value]
          [:a {:href url
               :target "_blank"} title])))))

;; 'urls' of internal navigation(instead of opening in a new tab), such as Suite name in instances view
(gc/register-field-type!
  :internal-url
  simple-header
  (fn
    [{:keys [field]} row]
    (let [value (get row field)]
      (cond 
        (map? value)
        (:title value) 
        
        :else
        (str value))))
  (fn [field-data row]
    (let [custom-field-key (:field field-data)
          value (get row custom-field-key)]
      (cond
        (string? value)
        [:a {:href value} value]

        (map? value)
        (let [{:keys [url title]} value]
          [:a {:href url} title])))))

(gc/register-field-type!
  :multilist
  simple-header
  (fn [{:keys [field display-field] :as _} row]
    (let [values (get row (or display-field field))]
         (cond
           (empty? values)
           ""

           (and (string? values)
                (re-matches #"\[.*\]" values))
           (try
             (str/join "; " (js/JSON.parse values))
             (catch :default _
               values))

           (coll? values)
           (str/join "; " values)

           :else
           (str values))))
  (fn [{:keys [field display-field] :as _} row]
    (let [values (get row (or display-field field))]
      (cond
        (empty? values)
        ""

        (and (string? values)
             (re-matches #"\[.*\]" values))
        (try
          (str/join "; " (js/JSON.parse values))
          (catch :default _
            values))

        (coll? values)
        (str/join "; " values)

        :else
        (str values)))))

(defn compare-date-time
  [{:keys [field type] :as column-info} direction row-a row-b]
  ;; It can be either String like "2024-08-16T07:51:04Z" or it
  ;; can be a timestamp e.g. 1776930857531 here, either way it
  ;; sort of works with text representation (by accident)
  (gc/default-compare-fn column-info direction row-a row-b))

(gc/register-field-type!
  :date-time
  simple-header
  (fn [{:keys [field]} row]
    (get row field))
  (fn [{:keys [field]} row]
    (let [value (get row field)
          formatter-value (fu/formatted-date-time value)]
      formatter-value))
  gc/default-search-fn
  compare-date-time)

;; We can have either user map with full information
;; or just user_id passed to us - then we should look for user in app-db
;; Prefer the live project user, but fall back to the embedded map so that
;; users removed from the project still render by name (e.g. Tester column)
(defn- get-user-object [user-info users]
  (if (= (:type user-info) "group")
    user-info
    (if (map? user-info)
      (or (users/find-user users (:id user-info)) user-info)
      (users/find-user users user-info))))

(defn- get-user-text [user-object row field]
  (if user-object
    (if (= "group" (:type user-object))
      (str "Group: " (:name user-object))
      (:name user-object))
    (let [user-info (get row field)]
      (if (and user-info (or (= field :author) (= field :closed_by) (= field :user_id)))
        (or (case field
              :author (:author_user_name row)
              :closed_by (:closed_by_user_name row)
              :user_id (:user_name row)
              nil)
            "Unknown User")
        ""))))

(gc/register-field-type!
  :user
  simple-header
  (fn [{:keys [field users] :as column-info} row]
    (let [user-info (get row field)]
      (get-user-text (get-user-object user-info users) row field)))
  (fn [{:keys [field users] :as column-info} row]
    (let [user-info (get row field)
          smartfox? (boolean (get-in row [:meta :smartfox_used]))
          created-via-mcp? (boolean (get-in row [:meta :created_via_mcp]))
          updated-via-mcp? (boolean (get-in row [:meta :updated_via_mcp]))
          ai-text-enhancement? (boolean (get-in row [:meta :ai_text_enhancement_used]))
          smartfox-icon (fn [props]
                          [:span.smartfox-icon-in-grid
                           props
                           [icons/mux-smart-fox2]])
          tooltip-text (cond
                         created-via-mcp? (get-translation "ai.created-via-mcp.tooltip"
                                                           "Created using AI MCP")
                         updated-via-mcp? (get-translation "ai.updated-via-mcp.tooltip"
                                                           "Updated using AI MCP")
                         smartfox? (get-translation "ai.smartfox-used.tooltip"
                                                    "Built and updated with Step Generator assistance")
                         ai-text-enhancement? (get-translation "ai.text-enhancement-used.tooltip"
                                                               "Text Updated with AI assistance"))]
      [:div {:class (when tooltip-text "user-name-with-ai-icon")}
       [:span {:class "user-name"} (get-user-text (get-user-object user-info users) row field)]
       (when tooltip-text
         [tooltip/mux-tooltip
          {}
          tooltip-text
          smartfox-icon])])))

(gc/register-field-type!
  :run_status
  simple-header
  (fn [{:keys [field] :as column-info} row]
    (let [status (get row field)]
      (run-status/get-status-label status)))
  (fn [{:keys [field] :as column-info} row]
    (when-let [status (get row field)]
      [run-status/run-status-component status])))

(gc/register-field-type!
 :requirement_status
 simple-header
 (fn [{:keys [field] :as column-info} row]
   (let [status (get row field)]
     (requirement-testing-status/get-status-label status)))
 (fn [{:keys [field] :as column-info} row]
   (when-let [status (get row field)]
     [requirement-testing-status/requirement-status-component status])))

(gc/register-field-type!
 :has_issue_indicator
 simple-header
 (fn [{:keys [field] :as column-info} row]
   (when (:has_linked_issues row)
     [icons/mux-issue]))
 (fn [{:keys [field] :as column-info} row]
   (when (:has_linked_issues row)
     (let [{:keys [url title]} row]
       [:a {:href (str url "#traceability_tab")}
        [icons/mux-issue]]))))

(gc/register-field-type!
  :has_attachment_indicator
  simple-header
  (fn [_ row]
    (when (:has_attachment row)
      [icons/mux-paperclip]))
  (fn [_ row]
    (when (:has_attachment row)
      (let [{:keys [url]} row]
        [:a {:href (str url "#attachments")}
         [icons/mux-paperclip]]))))

(gc/register-field-type!
 :linked-entities
 simple-header
 vector-of-maps-text
 ;; Old implementation
 (fn [{:keys [field new-tab?] :as _column-info} row]
   (let [entities (get row field)
         entities (if (= field :linked_milestones)
                    (map (fn [item]
                           (if (map? item)
                             {:id (or (:id item) (first (vals item)))
                              :text (or (:text item) (:name item) (str item))}
                             (let [[id name] item]
                               {:id id
                                :text name})))
                         entities)
                    entities)
         ;; shows whether or not the "Use PractiTest ID for synced Issues" check box is selected.
         integ_internal_id (:integ_internal_id @(re-frame/subscribe [::subs/current-project]))
         get-display-value (fn [text external_id] (if integ_internal_id text (or external_id text)))
         links (doall
                 (for [{:keys [id external_id text highlight external url name status]} entities]
                   (with-meta
                     (if id
                       (let [final-url (if-not (str/blank? url)
                                         url
                                         (case field
                                           :cached_linked_issues (get-url-view "issues#show" {:id id})
                                           :linked_issues (get-url-view "issues#edit" {:id id})
                                           :linked_tests (get-url-view "qtest_designs#edit" {:id id})
                                           :linked_testsets (get-url-view "suites#edit" {:id id})
                                           :linked_requirements (get-url-view "requirements#edit" {:id id})
                                           :linked_milestones (get-url-view "milestones#edit" {:id id})
                                           nil))
                             base-text (get-display-value text external_id)
                             link-element [:a.nowrap
                                           {:href   final-url
                                            :class  (when highlight "fw-bold")
                                            :target (when (or external new-tab?) "_BLANK")}
                                           base-text]
                             status-element (when (and status
                                                       (or (= field :linked_issues)
                                                           (= field :cached_linked_issues)))
                                              [:span " (" status ")"])
                             tooltip-text (:title name)]
                        (if (and tooltip-text (not (str/blank? tooltip-text)))
                          [tooltip/mux-tooltip
                           {}
                           tooltip-text
                           (fn [props] [:span props link-element status-element])]
                          [:span.nowrap link-element status-element]))
                       [:span.nowrap
                        {:class (when highlight "fw-bold")}
                        (get-display-value text external_id)])
                     {:key text})))]
     [:<>
      (interpose ", " links)])))

(defn- format-parameters-text
  [params]
  (str/join "," (map (fn [[k v]]
                       (str (name k) ": " v))
                     (seq params))))

(gc/register-field-type!
  :parameters
  simple-header
  (fn [{:keys [field] :as _column-info} row]
    (let [params (get row field)]
     (format-parameters-text params)))
  (fn [{:keys [field] :as _column-info} row]
    (let [params (get row field)]
      (if (seq params)
        [:<>
         [icons/mux-parameters-set]
         " "
         (format-parameters-text params)]
        [icons/mux-parameters-empty]))))

(gc/register-field-type!
  :tags
  simple-header
  tags-text
  (fn [{:keys [field]} row]
    (let [values (get row field)]
      (str/join ", " values))))

(gc/register-field-type!
  :version_label
  simple-header
  labels-text
  labels-text)

(filters/register-filter!
  :version_label
  filters/list-view-dispatcher-fn
  (fn [filter-state column-info row]
    (let [cell-value (get row (:field column-info))
          label-names (map :name cell-value)
          filter-names (set (map #(if (vector? %) (last %) %) filter-state))]
      (or
        (boolean (some filter-names label-names))
        (and (contains? filter-state "")
             (empty? cell-value)))))
  filters/list-value-info)

(defn get-folder-name [{:keys [field]} row]
  (or (get-in row [(when-let [ns (namespace field)]
                     (keyword ns "folder"))
                   :name])
      (get-in row [:folder :name])
      ""))

(gc/register-field-type!
 :folder_id
 simple-header
 (fn [column-info row] (get-folder-name column-info row))
 (fn [column-info row] (get-folder-name column-info row)))

(gc/register-field-type!
  :status-bar
  simple-header
  (constantly nil)
  (fn [{:keys [field]} row]
    (let [statuses (filter (fn [[k _]] (some? k)) (get row field))
          test-type  (:test/test_type row)]
  [statuses-bar statuses
   (fn [status]
     (run-status/get-status-color (name status) (= test-type "ExploratoryTest")))])))

(gc/register-field-type!
 :testing-scope
 simple-header
 simple-text
 (fn [{:keys [field] :as column-info} row]
   (let [scope (get row field)]
     (cond
       (= scope "LAST_RUN") (:LAST_RUN (testing-coverage/testing-scope-labels))
       (= scope "TESTSETS") (:TESTSETS (testing-coverage/testing-scope-labels))
       :else nil))))

(defn- render-testset-instance-name
  "Renders the name column for testset/instance grid in requirement traceability"
  [row]
  (if (:testset-header? row)
    ;; Testset header: show testset icon + name + pagination
    (let [testset-id (:id row)
          collapsed? @(re-frame/subscribe [::testing-coverage/testset-collapsed? testset-id])
          loading? @(re-frame/subscribe [::testing-coverage/testset-loading? testset-id])
          current-page @(re-frame/subscribe [::testing-coverage/testset-instances-page testset-id])
          testset-name (:title (:name row))]
      [:div.testset-header
       [:span.testset-icon
        [icons/mux-testsets-runs]]
       (when loading?
         [:span.testset-loading-spinner
          [spinners/mux-spinner {:size :text}]])
       (if (and testset-name (not (str/blank? testset-name)))
         [overflow/overflow-with-mux-tooltip
          {}
          (fn [props]
            [:a (merge props
                       {:href (get-in row [:name :url])
                        :target "blank"})
             testset-name])
          testset-name]
         [:a {:href (get-in row [:name :url])
              :target "blank"}
          testset-name])
       (when-not collapsed?
         [pagination/pagination-view
          {:current-page current-page
           :total-count (or (:filtered_instances_count row) 0)
           :per-page 5
           :max-buttons 0
           :always-show-nav? true
           :disabled? collapsed?
           :on-change #(re-frame/dispatch [::testing-coverage/set-testset-instances-page testset-id %])}])])
    ;; Instance row: use existing hierarchical child styling
    [:div.hierarchical-child-cell
     [:span.instance-icon
      [icons/mux-test-library]]
     [:a {:href (get-in row [:name :url])
          :target "blank"}
      (:title (:name row))]]))

(gc/register-field-type!
  :hierarchical-name
  (fn [column] [:span (:header column)])
  (fn [{:keys [field]} row]
    (get-in row [field :title]))
  (fn [_column row]
    (render-testset-instance-name row))
  gc/default-search-fn
  ;; Custom compare function that uses the name-sort field for proper display ID sorting
  (fn [{:keys [field] :as column-info} direction row-a row-b]
    (compare-display-id (assoc column-info :field :name-sort) direction row-a row-b)))

(gc/register-field-type!
  :testset-instance-id
  (fn [column] [:span (:header column)])
  (fn [{:keys [field]} row]
    (get row field))
  (fn [{:keys [field]} row]
    (if (:testset-header? row)
      ;; Testset row: show display_id with chevron
      (let [testset-id (:id row)
            collapsed? @(re-frame/subscribe [::testing-coverage/testset-collapsed? testset-id])
            has-instances? (pos? (or (:instances_count row) 0))]
        [:div.display-id-with-arrow
         [:span (get row field)]
         [:span.chevron
          (merge
           {:class (when-not has-instances? "disabled")}
           (when has-instances?
             {:on-click #(re-frame/dispatch [::testing-coverage/toggle-testset-collapse testset-id])}))
          (if collapsed?
            [icons/mux-chevron-right]
            [icons/mux-chevron-down2])]])
      ;; Instance row: just show display_id
      [:span (get row field)]))
  gc/default-search-fn
  gc/default-compare-fn)

(gc/register-field-type!
  :testset-instance-run-status
  (fn [column] [:span (:header column)])
  (fn [{:keys [field]} row]
    (get row field))
  (fn [{:keys [field]} row]
    (if (:testset-header? row)
      ;; Testset row: show status bar
      (when-let [status-bar (:run_status_bar row)]
        [statuses-bar
         status-bar
         (fn [status]
           (run-status/get-status-color (name status) false))])
      ;; Instance row: show run status
      (when-let [status (get row field)]
        [run-status/run-status-component status])))
  gc/default-search-fn
  gc/default-compare-fn)

(gc/register-field-type!
  :duration
  simple-header
  (fn [{:keys [field] :as column-info} row]
    (let [value (or (get row field) 0)
          formatted-value (fu/format-duration (fu/parse-duration value))]
      ;; Duration is exported as simple integer right now
      ;; TODO: add string support if required ?
      formatted-value))
  (fn [{:keys [field] :as column-info} row]
    (let [value (get row field)
          formatted-value (fu/format-duration (fu/parse-duration value))]
      formatted-value)))

(gc/register-field-type!
  :custom
  ;; Header
  (fn [{:keys [header-fn header]
        :as   column-info}]
    (or header
        (header-fn column-info)))
  ;; Text
  (fn [_ _]
    ;; No need to filter/sort here
    nil)
  ;; Cell
  (fn [{:keys [cell-fn] :as column-info} row grid-props]
    (cell-fn column-info row grid-props)))

;; Treat 0 as nils for sorting purposes due to the way they are stored internally now
(defn- parse-value-score
  [s]
  (let [parsed (fu/try-parse-float s 0.0)]
    (if (= parsed 0)
      nil
      parsed)))

(defn- compare-value-scores
  [{:keys [field] :as column-info} direction row-a row-b]
  (let [value-a (get row-a field)
        value-b (get row-b field)]
    (oldgrid/nulls-last-sort direction (parse-value-score value-a) (parse-value-score value-b))))

(gc/register-field-type!
  :value_score
  simple-header
  simple-text
  (fn [{:keys [field] :as column-info} row]
    (let [score (get row field)]
      [value-score/value-score-field score]))
  gc/default-search-fn
  compare-value-scores)

(gc/register-field-type!
  :memo
  simple-header
  simple-text
  (fn [{:keys [field] :as _} row]
    (let [value (get row field)]
      [markdown-preview value])))

(gc/register-field-type!
  :kanban_lane
  simple-header
  (fn [{:keys [field] :as _} row]
    (let [value (get row field)]
      (:name value)))
  (fn [{:keys [field] :as _} row]
    (let [value (get row field)]
      (:name value))))

(gc/register-field-type!
  :global_field_type
  simple-header
  simple-text
  (fn [{:keys [field] :as _} row]
      (let [field-type (get row field)]
           (get-field-format-label field-type))))